// AdminSlateLearningPage.tsx — (tf3-42, B90; Creative 2 C2.2-1 §3, planning 10-21 16:45 #1) Admin → Slates → Learning:
// what the slates learned for an audience (growth's account): the weights the next slate ranks with and why, the judged
// pilots, the current set's tests, each dimension's weight, the pilots it learned from, and the earlier sets. Opened from a
// slate board's weights line with ?audienceId=…&set=…: the set its rank used is marked (an earlier one by its summary).
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageContainer, TfCard } from "@/components/v2/primitives.tsx";
import { engineSlatesApi } from "@/api/engine-slates.ts";
import { slateRefusalText, slateAudienceOk } from "@/lib/slates.ts";
import { judgedLine, rankTestLine, producibilityLine, setOf, type LearningView } from "@/lib/learning.ts";

const NO_AUDIENCE = "";
export function AdminSlateLearningPage() {
  const [params, setParams] = useSearchParams();
  const audienceId = params.get("audienceId") ?? NO_AUDIENCE;
  const setId = params.get("set");
  const [profiles, setProfiles] = useState<{ id: string; label: string }[]>([]);
  const [v, setV] = useState<LearningView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { engineSlatesApi.audiences().then((r) => setProfiles(r.profiles)).catch(() => setProfiles([])); }, []);   // catch-reason: the picker keeps "No audience"; the read below still works
  useEffect(() => {
    let alive = true;   // a late answer for another audience is dropped
    setV(null); setErr(null);
    engineSlatesApi.learning(audienceId || null).then((r) => { if (alive) setV(r); }).catch((e) => { if (alive) setErr(slateRefusalText(e)); });   // catch-reason: shown in place of the read
    return () => { alive = false; };
  }, [audienceId]);
  const marked = v ? setOf(v, setId) : null;
  const c = v?.current ?? null;
  return (
    <PageContainer title="Learning" subtitle="What the slates learned from their pilots, per audience (growth's account).">
      <label style={{ fontSize: 13 }}>Audience{" "}
        <select value={audienceId} onChange={(e) => setParams(e.target.value ? { audienceId: e.target.value } : {})}>
          <option value={NO_AUDIENCE}>No audience</option>
          {audienceId && !profiles.some((p) => p.id === audienceId) && <option value={audienceId}>{audienceId}</option>}
          {profiles.map((p) => <option key={p.id} value={p.id} disabled={!slateAudienceOk(p.id)}>{p.label}</option>)}
        </select></label>
      {err && <div style={{ color: "#b91c1c", marginTop: 8 }}>The learning couldn't be read: {err}</div>}
      {!v && !err && <div className="tf-muted" style={{ marginTop: 8 }}>Reading…</div>}
      {v && <>
        <TfCard>
          <div style={{ fontSize: 15 }}><strong>{v.note}</strong></div>
          <div style={{ fontSize: 13, marginTop: 4 }}>{judgedLine(v)}</div>
          {marked?.kind === "earlier" && <div style={{ fontSize: 13, marginTop: 4, color: "#92400e" }}>The slate you came from ranked with {marked.line}.</div>}
          {marked?.kind === "unknown" && <div style={{ fontSize: 13, marginTop: 4, color: "#92400e" }}>The slate you came from ranked with a set this audience doesn't list ({setId}).</div>}
        </TfCard>
        {c && <TfCard>
          <h3 style={{ marginTop: 0 }}>The current set{marked?.kind === "current" ? " (the one the slate you came from used)" : ""}</h3>
          <div className="tf-muted" style={{ fontSize: 12 }}>{c.setId} · {c.createdAt.slice(0, 16).replace("T", " ")} · {c.pilots} pilots · {c.version} · views over {c.minViews} within {c.windowHours} h</div>
          <ul style={{ fontSize: 13, paddingLeft: 18 }}>
            <li>{rankTestLine(c)}</li>
            <li>{producibilityLine(c)}</li>
            {c.reclamped.length > 0 && <li>Held to their bounds: {c.reclamped.join(", ")}</li>}
            <li>{c.reason}</li>
          </ul>
          <table style={{ fontSize: 12, borderCollapse: "collapse" }}>
            <thead><tr><th align="left">Dimension</th><th>Starting</th><th>Learned</th><th>ρ</th><th>n</th><th align="left">Why</th></tr></thead>
            <tbody>{c.dimensions.map((d) => <tr key={d.dimension}><td>{d.dimension.replace(/_/g, " ")}</td><td align="center">{d.defaultWeight}</td>
              <td align="center">{d.weight}</td><td align="center">{d.rho === null ? "—" : d.rho.toFixed(2)}</td><td align="center">{d.n}</td><td>{d.note}</td></tr>)}</tbody>
          </table>
          <h4>The pilots it learned from</h4>
          <ul style={{ fontSize: 12, paddingLeft: 18 }}>{c.learnedFrom.map((p) => <li key={`${p.slateId}:${p.ideaId}`}>
            <Link to={`/admin/slates/${p.slateId}`}>{p.ideaId}</Link> · <Link to={`/admin/runs?run=${p.runId}`}>its run</Link> · target {p.target.toFixed(2)}, scored {p.total === null ? "—" : p.total.toFixed(2)}</li>)}</ul>
        </TfCard>}
        {v.history.length > 0 && <TfCard>
          <h3 style={{ marginTop: 0 }}>The sets so far (newest first)</h3>
          <ul style={{ fontSize: 12, paddingLeft: 18 }}>{v.history.map((h) => <li key={h.setId} style={h.setId === setId ? { fontWeight: 600 } : undefined}>
            {h.createdAt.slice(0, 10)} · {h.pilots} pilots · {h.rankCorrelation === null ? "rank not measured" : `ρ ${h.rankCorrelation.toFixed(2)}`} · <span className="tf-muted">{h.setId}</span></li>)}</ul>
        </TfCard>}
      </>}
    </PageContainer>
  );
}
